import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { BrowserRouter, Routes, Route, useNavigate, useLocation, Navigate } from 'react-router-dom';
import LoginPage from './pages/LoginPage';
import AdminPage from './pages/AdminPage';
import DashboardPage from './pages/DashboardPage';
import CockpitPage from './pages/CockpitPage';
import ReviewPage from './pages/ReviewPage';
import BudgetPage from './pages/BudgetPage';
import BankingPage from './pages/BankingPage';
import ContractsPage from './pages/ContractsPage';
import KuendigungsPage from './pages/KuendigungsPage';
import ContractDetailPage from './pages/ContractDetailPage';
import AnomaliesPage from './pages/AnomaliesPage';
import BuchungenPage from './pages/BuchungenPage';
import ImportPage from './pages/ImportPage';
import ImportCenterPage from './pages/ImportCenterPage';
import AuswertungPage from './pages/AuswertungPage';
import GeldfresserPage from './pages/GeldfresserPage';
import EmpfaengerPage from './pages/EmpfaengerPage';
import RulesPage from './pages/RulesPage';
import ReportsPage from './pages/ReportsPage';
import ReportCenterPage from './pages/ReportCenterPage';
import ClosingPage from './pages/ClosingPage';
import SystemPage from './pages/SystemPage';
import HelpPage from './pages/HelpPage';
import WiederkehrendPage from './pages/WiederkehrendPage';
import ForecastPage from './pages/ForecastPage';
import ZahlungsvorschauPage from './pages/ZahlungsvorschauPage';
import UserManagementPage from './pages/UserManagementPage';
import OptimizationPage from './pages/OptimizationPage';
import api from './api';

const tabs = [
  { path: '/dashboard', icon: 'OV', label: 'Übersicht', title: 'Übersicht' },
  { path: '/cockpit', icon: 'CP', label: 'Cockpit', title: 'Finanz-Cockpit' },
  { path: '/review', icon: 'RV', label: 'Review', title: 'Finanz-Review' },
  { path: '/optimierung', icon: 'SP', label: 'Sparen', title: 'Finanzoptimierung' },
  { path: '/budgets', icon: 'BZ', label: 'Budgets', title: 'Budget-Ziele' },
  { path: '/wiederkehrend', icon: 'WK', label: 'Wiederkehrend', title: 'Wiederkehrende Zahlungen' },
  { path: '/prognose', icon: 'PR', label: 'Prognose', title: 'Cashflow-Prognose' },
  { path: '/zahlungen', icon: 'ZV', label: 'Vorschau', title: 'Zahlungsvorschau' },
  { path: '/banking', icon: 'BK', label: 'Banking', title: 'Bank-Sync' },
  { path: '/contracts', icon: 'VR', label: 'Verträge', title: 'Abo- und Vertragsradar' },
  { path: '/kuendigungen', icon: 'KZ', label: 'Kündigen', title: 'Kündigungszentrale' },
  { path: '/anomalies', icon: 'AR', label: 'Anomalien', title: 'Anomalie-Radar' },
  { path: '/import', icon: 'UP', label: 'Import', title: 'Neuer Import' },
  { path: '/imports', icon: 'FI', label: 'Dateien', title: 'Import- und Datei-Center' },
  { path: '/buchungen', icon: 'BU', label: 'Buchungen', title: 'Buchungen' },
  { path: '/auswertung', icon: 'AN', label: 'Analyse', title: 'Analyse' },
  { path: '/rules', icon: 'RG', label: 'Regeln', title: 'Regelkontrolle' },
  { path: '/reports', icon: 'BE', label: 'Berichte', title: 'Berichte und Snapshots' },
  { path: '/report-center', icon: 'RC', label: 'Berichts-Center', title: 'Berichts-Center' },
  { path: '/abschluss', icon: 'MA', label: 'Abschluss', title: 'Monatsabschluss' },
  { path: '/geldfresser', icon: 'GF', label: 'Prüfen', title: 'Geldfresser prüfen' },
  { path: '/users', icon: 'NV', label: 'Nutzer', title: 'Nutzerverwaltung' },
  { path: '/system', icon: 'SY', label: 'System', title: 'Systemkonsole' },
  { path: '/admin', icon: 'AD', label: 'Admin', title: 'Administration' },
];

const helpText = {
  '/dashboard': 'Startpunkt für den schnellen Lageblick. Für Entscheidungen ist das Cockpit meist aussagekräftiger.',
  '/cockpit': 'Zeigt Trends, Warnungen, Fixkosten und auffällige Empfänger. Gut für den monatlichen Check.',
  '/review': 'Eine zentrale Inbox für offene Entscheidungen aus Geldfressern, Verträgen, Budgets und Anomalien.',
  '/optimierung': 'Bewerte jede Ausgabe nach Notwendigkeit, Sparpotenzial und konkreter Maßnahme. Bestehende Buchungen bleiben unverändert erhalten.',
  '/budgets': 'Monatslimits je Kategorie setzen. Die Ampeln erscheinen danach im Cockpit und Monatsabschluss.',
  '/wiederkehrend': 'Automatisch erkannte und manuell gepflegte wiederkehrende Zahlungen verwalten. Kandidaten bestätigen bevor sie in die Prognose einfließen.',
  '/prognose': 'Voraussichtlicher Cashflow basierend ausschließlich auf bestätigten wiederkehrenden Zahlungen. Kein garantierter Kontostand.',
  '/zahlungen': 'Geplante Abbuchungen im frei wählbaren Zeitraum. Basiert auf bestätigten wiederkehrenden Serien. Bereits gebuchte Transaktionen werden erkannt.',
  '/banking': 'Vorbereitung für PSD2/ING-Sync. Ohne API-Zugangsdaten werden keine Bankdaten abgerufen.',
  '/contracts': 'Wiederkehrende Abbuchungen erkennen und als behalten, prüfen oder kündigen markieren.',
  '/kuendigungen': 'Alle zur Kündigung vorgemerkten Verträge. Schreiben vorbereiten, Status verfolgen, kein automatischer Versand.',
  '/anomalies': 'Neue Empfänger, Betragsausreißer, Dubletten und Kategorie-Sprünge prüfen.',
  '/import': 'Hier kommen neue Bankdateien hinein. Nach dem Import die Datei im Datei-Center kontrollieren.',
  '/imports': 'Importe nachvollziehen, Exporte laden und neue Dateien prüfen. Alte Importe ohne Import-ID bleiben geschützt.',
  '/buchungen': 'Einzelbuchungen prüfen, suchen und bei Bedarf mit Flags oder Notizen versehen.',
  '/auswertung': 'Klassische Auswertungen und Kategorieübersichten.',
  '/rules': 'Regeln testen, Beispiele ansehen und kontrolliert anwenden. Ideal, wenn Kategorien nicht sauber sitzen.',
  '/reports': 'Monatsbericht ansehen, drucken, Jahresbericht prüfen und Snapshots vergleichen.',
  '/abschluss': 'Geführter Monatscheck mit Sparpotenzial, Warnungen und Abschluss-Snapshot.',
  '/geldfresser': 'KI- und datenbasierte Vorschläge prüfen und per Checkbox bestätigen.',
  '/empfaenger': 'Detailprüfung mit Verlauf, Regeln, Notizen und Flags.',
  '/users': 'Benutzer anlegen, Rollen vergeben, Passwörter zurücksetzen und Konten deaktivieren. Nur für Administratoren.',
  '/system': 'Healthcheck, Datenbankstruktur, Uploads, Events und Reparaturfunktionen für Wartung.',
  '/help': 'Kompakter Arbeitsleitfaden für die stabilisierte Finanzanalyse.',
};

function currentTab(pathname) {
  return tabs.find(t => pathname === t.path || pathname.startsWith(t.path + '/')) ||
    (pathname.startsWith('/help') ? { path: '/help', title: 'Hilfe' } : tabs[0]);
}

function Nav() {
  const nav = useNavigate();
  const loc = useLocation();
  return (
    <nav className="bottom-nav" aria-label="Hauptnavigation">
      {tabs.map(t => {
        const active = loc.pathname === t.path || loc.pathname.startsWith(t.path + '/');
        return (
          <button key={t.path} className={active ? 'nav-item active' : 'nav-item'} onClick={() => nav(t.path)} title={t.title}>
            <span className="nav-icon">{t.icon}</span>
            <span>{t.label}</span>
          </button>
        );
      })}
    </nav>
  );
}

function StatusPill({ health }) {
  if (!health) return <span className="status-pill muted">Prüfe System</span>;
  if (health.status !== 'ok') return <span className="status-pill danger">System prüfen</span>;
  const count = health.database?.stats?.buchungen;
  return <span className="status-pill good">Stabil · {count ?? '-'} Buchungen</span>;
}

function ContextHelp({ pathname }) {
  const [open, setOpen] = useState(() => localStorage.getItem('fa_help_open') === '1');
  const text = helpText[currentTab(pathname).path] || helpText['/dashboard'];
  useEffect(() => localStorage.setItem('fa_help_open', open ? '1' : '0'), [open]);
  return (
    <section className={open ? 'context-help open' : 'context-help'}>
      <button className="context-help-toggle" onClick={() => setOpen(v => !v)} aria-expanded={open}>
        <span>Was ist hier wichtig?</span>
        <strong>{open ? 'Schließen' : 'Öffnen'}</strong>
      </button>
      {open && <p>{text}</p>}
    </section>
  );
}

function AppHeader({ health, onLogout }) {
  const nav = useNavigate();
  const loc = useLocation();
  const tab = currentTab(loc.pathname);
  return (
    <header className="app-header">
      <div>
        <p className="app-kicker">Finanzanalyse</p>
        <h1>{tab.title}</h1>
      </div>
      <div className="header-actions">
        <StatusPill health={health} />
        <button className="ghost-button" onClick={() => nav('/review')}>Review</button>
        <button className="ghost-button" onClick={() => nav('/banking')}>Banking</button>
        <button className="ghost-button" onClick={() => nav('/budgets')}>Budgets</button>
        <button className="ghost-button" onClick={() => nav('/contracts')}>Vertraege</button>
        <button className="ghost-button" onClick={() => nav('/anomalies')}>Anomalien</button>
        <button className="ghost-button" onClick={() => nav('/geldfresser')}>Geldfresser</button>
        <button className="ghost-button" onClick={() => nav('/abschluss')}>Abschluss</button>
        <button className="ghost-button" onClick={() => nav('/reports')}>Bericht</button>
        <button className="ghost-button" onClick={() => nav('/help')}>Hilfe</button>
        <button className="ghost-button" onClick={() => nav('/system')}>System</button>
        <button className="ghost-button" onClick={() => nav('/users')}>Nutzer</button>
        <button className="ghost-button" onClick={() => nav('/admin')}>Admin</button>
        <button className="ghost-button" onClick={onLogout} title="Abmelden">Abmelden</button>
      </div>
    </header>
  );
}

function Guard({ children }) {
  const loc = useLocation();
  const nav = useNavigate();
  const [authState, setAuthState] = useState('loading');
  const [health, setHealth] = useState(null);

  const checkAuth = useCallback(() => {
    let cancelled = false;
    api.get('/auth/me')
      .then(r => {
        if (!cancelled) {
          const mustChange = Boolean(r.data?.user?.force_password_change);
          if (mustChange && !(loc.pathname === '/admin' && new URLSearchParams(loc.search).get('tab') === 'sicherheit')) {
            nav('/admin?tab=sicherheit&first=1', { replace: true });
            return;
          }
          setAuthState('ok');
          setHealth(r.data.health || null);
        }
      })
      .catch(err => {
        if (!cancelled) {
          if (err.response?.status === 401) {
            setAuthState('denied');
          } else {
            setAuthState('ok');
          }
        }
      });
    return () => { cancelled = true; };
  }, [loc.pathname, loc.search, nav]);

  useEffect(() => {
    return checkAuth();
  }, [checkAuth]);

  async function handleLogout() {
    try { await api.post('/logout'); } catch {}
    nav('/');
  }

  if (authState === 'loading') return (
    <div className="app-shell" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '100vh' }}>
      <span>Authentifizierung...</span>
    </div>
  );
  if (authState === 'denied') return <Navigate to="/" replace />;

  return (
    <div className="app-shell">
      <AppHeader health={health} onLogout={handleLogout} />
      <ContextHelp pathname={loc.pathname} />
      <main className="app-main">{children}</main>
      <Nav />
    </div>
  );
}

function RootRedirect() {
  const [checking, setChecking] = useState(true);
  const [authed, setAuthed] = useState(false);

  useEffect(() => {
    api.get('/auth/me')
      .then(() => { setAuthed(true); setChecking(false); })
      .catch(() => { setAuthed(false); setChecking(false); });
  }, []);

  if (checking) return null;
  return authed ? <Navigate to="/cockpit" replace /> : <LoginPage />;
}

export default function App() {
  const routes = useMemo(() => ([
    ['/', <RootRedirect />],
    ['/dashboard', <Guard><DashboardPage /></Guard>],
    ['/cockpit', <Guard><CockpitPage /></Guard>],
    ['/review', <Guard><ReviewPage /></Guard>],
    ['/optimierung', <Guard><OptimizationPage /></Guard>],
    ['/budgets', <Guard><BudgetPage /></Guard>],
    ['/wiederkehrend', <Guard><WiederkehrendPage /></Guard>],
    ['/prognose', <Guard><ForecastPage /></Guard>],
    ['/zahlungen', <Guard><ZahlungsvorschauPage /></Guard>],
    ['/banking', <Guard><BankingPage /></Guard>],
    ['/contracts', <Guard><ContractsPage /></Guard>],
    ['/contracts/:id', <Guard><ContractDetailPage /></Guard>],
    ['/kuendigungen', <Guard><KuendigungsPage /></Guard>],
    ['/anomalies', <Guard><AnomaliesPage /></Guard>],
    ['/import', <Guard><ImportPage /></Guard>],
    ['/imports', <Guard><ImportCenterPage /></Guard>],
    ['/buchungen', <Guard><BuchungenPage /></Guard>],
    ['/auswertung', <Guard><AuswertungPage /></Guard>],
    ['/rules', <Guard><RulesPage /></Guard>],
    ['/reports', <Guard><ReportsPage /></Guard>],
    ['/report-center', <Guard><ReportCenterPage /></Guard>],
    ['/abschluss', <Guard><ClosingPage /></Guard>],
    ['/geldfresser', <Guard><GeldfresserPage /></Guard>],
    ['/empfaenger', <Guard><EmpfaengerPage /></Guard>],
    ['/users', <Guard><UserManagementPage /></Guard>],
    ['/system', <Guard><SystemPage /></Guard>],
    ['/help', <Guard><HelpPage /></Guard>],
    ['/admin', <Guard><AdminPage /></Guard>],
  ]), []);

  return (
    <BrowserRouter>
      <Routes>
        {routes.map(([path, element]) => <Route key={path} path={path} element={element} />)}
        <Route path="*" element={<Navigate to="/cockpit" replace />} />
      </Routes>
    </BrowserRouter>
  );
}
